import { useEffect, useRef, useState } from "react";
import tw from "tailwind-styled-components";
import { Formik, Field, Form } from "formik";
import { MdClose, MdEdit } from "react-icons/md";
import {
    InputGroup,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    ModalHeader,
} from "../Styles/InputStyles";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import DropZone from "../Notice/Dropzone";
import Images from "../../Images";
import CategoryCard from "../CategoryCard";
import MultiPropSelectModal from "../MultiPropSelectModal";
import { useSelector } from "react-redux";

const UploadMenu = ({
    setIsUploadMenuVisible,
    uploadMenuMutate,
    uploadMenuLoading,
    propId,
    selectedData,

}) => {
    const formikRef = useRef(null);
    const [height, setHeight] = useState(window.innerHeight);
    const [showPropertyModal, setShowPropertyModal] = useState(false);
    const property = useSelector((state) => state.PropertyReducer.property);
    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Filter out properties with id === 0
    const filteredProperties = property?.filter(prop => prop.id !== 0) || [];

    const initialValues = {
        file: null,
        properties: propId ? (Array.isArray(propId) ? propId : [propId]) : [],
    };

    const handleSubmit = (values, formikHelpers) => {
        if (!values.file) {
            toast.error("Menu is required");
            return;
        }

        if (!values.properties || values.properties.length === 0) {
            toast.error("At least one property is required");
            return;
        }
        const formData = new FormData();
        formData.append("file", values.file[0]);
        formData.append("propIds", values.properties.join(","));
        if (selectedData?.id) {
            formData.append("menuId", selectedData.id);
        }
        uploadMenuMutate(formData);
    };

    const handleClose = () => {
        setIsUploadMenuVisible(false);
    };

    const renderSelectedProperties = (selected = []) => {
        if (!selected || selected.length === 0) return "Select Properties";
        const selectedProperties = filteredProperties?.filter((prop) => selected.includes(prop.id));
        if (!selectedProperties?.length) return "Select Properties";
        const maxToShow = 2;
        const namesToShow = selectedProperties.slice(0, maxToShow).map((prop) => prop.name);
        const remainingCount = selectedProperties.length - maxToShow;
        return (
            <div className="flex items-center gap-1 truncate">
                {namesToShow.join(", ")}
                {remainingCount > 0 && <span className="ml-1 text-gray-500">+{remainingCount}</span>}
            </div>
        );
    };

    const handlePropertySave = (properties) => {
        if (formikRef.current) {
            formikRef.current.setFieldValue("properties", properties);
        }
        setShowPropertyModal(false);
    };

    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Upload Menu</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 flex flex-col justify-between">
                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ setFieldValue, values, resetForm }) => {
                        return (
                            <Form>
                                <ScrollableContent className="!max-h-[calc(100dvh-140px)]">
                                    <Wrapper>
                                        {/* Property Selection */}
                                        <InputGroup>
                                            <Label>Select Property/Properties *</Label>
                                            <CategoryCard
                                                type="button"
                                                selectedCategory={values.properties && values.properties.length > 0 ? {
                                                    label: renderSelectedProperties(values.properties),
                                                    value: "properties"
                                                } : null}
                                                title="Property/Properties"
                                                onClick={() => setShowPropertyModal(true)}
                                            />
                                            {(!values.properties || values.properties.length === 0) && (
                                                <ErrorText>At least one property is required</ErrorText>
                                            )}
                                        </InputGroup>

                                        {/* File Upload */}
                                        <InputGroup>
                                            <Label htmlFor="file">Menu *</Label>
                                            <FieldWrapper $select={true}>
                                                <Field
                                                    required
                                                    name="file"
                                                    type="text"
                                                    id="file"
                                                    autoComplete="off"
                                                >
                                                    {(props) => (
                                                        <DropZone
                                                            fields={props.field}
                                                            setFieldValue={props.form.setFieldValue}
                                                            componentFor="food-menu"
                                                            acceptedFiles=".xls,.xlsx,.xlsm,.xltx,.xlsb"
                                                            File={props.field.value ? [props.field.value] : null}
                                                        />
                                                    )}
                                                </Field>
                                            </FieldWrapper>
                                            <DemoFileLink>
                                                <a
                                                    href={Images.DemoFoodMenu}
                                                    download="DemoFoodMenu.xlsx"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <span>Sample Food Menu</span>
                                                </a>
                                            </DemoFileLink>
                                        </InputGroup>
                                    </Wrapper>
                                </ScrollableContent>
                                <ActionSection>
                                    <SideCancelBtn type="button" onClick={() => {
                                        resetForm();
                                        handleClose();
                                    }}>
                                        Cancel
                                    </SideCancelBtn>
                                    <SubmitBtn type="submit" disabled={uploadMenuLoading}>
                                        {uploadMenuLoading ? "Submitting..." : "Submit"}
                                    </SubmitBtn>
                                </ActionSection>

                                {/* Property Selection Modal */}
                                <MultiPropSelectModal
                                    isOpen={showPropertyModal}
                                    onClose={() => setShowPropertyModal(false)}
                                    onSelect={handlePropertySave}
                                    options={filteredProperties}
                                    selectedProperties={values.properties || []}
                                />
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const FieldWrapper = tw.div`w-full`;
const DemoFileLink = tw.div`flex justify-end mt-2 text-sm text-primary-color hover:underline hover:text-hover-color cursor-pointer`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5 mt-1`;

export default UploadMenu;